import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    CreditCard,
    User,
    Receipt,
    Globe,
    LifeBuoy,
    LogOut,
    Check,
    ChevronRight,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLang, LANGUAGES } from "@/lib/lang";

export default function UserMenu() {
    const { user, logout } = useAuth();
    const { lang, setLang } = useLang();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onClick(e) {
            if (!ref.current?.contains(e.target)) {
                setOpen(false);
                setLangOpen(false);
            }
        }
        function onKey(e) {
            if (e.key === "Escape") {
                setOpen(false);
                setLangOpen(false);
            }
        }
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const handleLogout = async (e) => {
        e.preventDefault();
        setOpen(false);
        await logout();
        navigate("/accueil");
    };

    const close = () => {
        setOpen(false);
        setLangOpen(false);
    };

    const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
    const initial = (user?.name?.[0] || user?.email?.[0] || "U").toUpperCase();

    return (
        <div className="relative" ref={ref} data-testid="user-menu">
            <button
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full pl-2 pr-1 py-1 hover:bg-black/5 transition"
                data-testid="user-menu-trigger"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <div className="hidden sm:flex flex-col items-end leading-tight">
                    <span className="text-sm font-medium text-[#1D1D1F]">{user?.name || "Utilisateur"}</span>
                    <span className="text-xs text-[#86868B] capitalize">Plan {user?.plan || "free"}</span>
                </div>
                {user?.picture ? (
                    <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                    <div className="w-9 h-9 rounded-full bg-[#1D1D1F] text-white flex items-center justify-center text-sm font-semibold">
                        {initial}
                    </div>
                )}
            </button>

            {open && (
                <div
                    role="menu"
                    data-testid="user-menu-dropdown"
                    className="absolute right-0 mt-3 w-[320px] tk-card overflow-hidden tk-menu-pop"
                    style={{ zIndex: 60 }}
                >
                    {/* User card */}
                    <div className="p-5 border-b border-black/5 flex items-center gap-3">
                        {user?.picture ? (
                            <img src={user.picture} alt="" className="w-11 h-11 rounded-full" />
                        ) : (
                            <div className="w-11 h-11 rounded-full bg-[#1D1D1F] text-white flex items-center justify-center text-base font-semibold">
                                {initial}
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="font-semibold text-[#1D1D1F] truncate">{user?.name || "Utilisateur"}</p>
                            <p className="text-xs text-[#86868B] truncate">{user?.email}</p>
                        </div>
                    </div>

                    {/* Plan summary */}
                    <Link
                        to="/app/forfait"
                        onClick={close}
                        className="block px-5 py-4 hover:bg-black/[0.03] transition"
                        data-testid="menu-forfait"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-[#86868B] font-bold">
                                    Forfait actuel
                                </p>
                                <p className="mt-1 font-semibold text-[#1D1D1F] capitalize">
                                    {user?.plan || "free"}
                                </p>
                            </div>
                            <span className="text-xs font-semibold text-[#007AFF] hover:underline">
                                Gérer →
                            </span>
                        </div>
                    </Link>

                    {/* Items */}
                    {!langOpen ? (
                        <div className="py-2 border-t border-black/5">
                            <MenuItem to="/app/account" icon={User} label="Paramètres du compte" onClick={close} testId="menu-account" />
                            <MenuItem to="/app/invoices" icon={Receipt} label="Factures et plans" onClick={close} testId="menu-invoices" />
                            <button
                                onClick={() => setLangOpen(true)}
                                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-black/[0.03] transition text-left"
                                data-testid="menu-language"
                            >
                                <Globe className="w-4 h-4 text-[#86868B]" />
                                <span className="text-sm text-[#1D1D1F] flex-1">Langue</span>
                                <span className="text-xs text-[#86868B]">
                                    {currentLang.flag} {currentLang.label}
                                </span>
                                <ChevronRight className="w-4 h-4 text-[#AEAEB2]" />
                            </button>
                            <MenuItem to="/app/aide" icon={LifeBuoy} label="Centre d'aide" onClick={close} testId="menu-help" />
                        </div>
                    ) : (
                        <div className="py-2 border-t border-black/5" data-testid="menu-lang-list">
                            <button
                                onClick={() => setLangOpen(false)}
                                className="w-full px-5 py-2 text-xs text-[#86868B] hover:text-[#1D1D1F] text-left"
                            >
                                ← Retour
                            </button>
                            {LANGUAGES.map((l) => (
                                <button
                                    key={l.code}
                                    onClick={() => {
                                        setLang(l.code);
                                        setLangOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-black/[0.03] transition text-left"
                                    data-testid={`menu-lang-${l.code}`}
                                >
                                    <span className="text-lg">{l.flag}</span>
                                    <span className="text-sm text-[#1D1D1F] flex-1">{l.label}</span>
                                    {lang === l.code && <Check className="w-4 h-4 text-[#34C759]" />}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Logout */}
                    <div className="border-t border-black/5 py-2">
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-5 py-3 hover:bg-black/[0.03] transition text-left text-[#FF3B30]"
                            data-testid="menu-logout"
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm font-medium">Se déconnecter</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function MenuItem({ to, icon: Icon, label, onClick, testId }) {
    return (
        <Link
            to={to}
            onClick={onClick}
            className="flex items-center gap-3 px-5 py-3 hover:bg-black/[0.03] transition"
            data-testid={testId}
        >
            <Icon className="w-4 h-4 text-[#86868B]" />
            <span className="text-sm text-[#1D1D1F] flex-1">{label}</span>
            <ChevronRight className="w-4 h-4 text-[#AEAEB2]" />
        </Link>
    );
}
